import { FormikProps, getIn } from 'formik'
import React from 'react'
import { CustomSelect, IOption } from '../Input/CustomSelect'
import { Label } from '../Label/Label'
// Import direto (e não pelo barrel `..`): o barrel reexporta este próprio arquivo, e a dependência
// circular obriga qualquer consumidor a carregar o app inteiro para usar um select.
import { ErrorLabel } from '../Label/ErrorLabel'
import { FontSize } from '../../../_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'

interface FormSelectProps {
  formik: FormikProps<any>
  name: string
  label?: string
  isMulti?: boolean
  /** Oculta a opção "Selecionar Todos" em selects múltiplos de lista fechada. */
  hideSelectAll?: boolean
  fetchOptions?: (
    inputValue: string,
    selectAll: boolean,
    extraParams?: Object,
    page?: number
  ) => Promise<{ options: IOption[]; hasMore: boolean }>
  options?: IOption[]
  placeholder?: string
  isClearable?: boolean
  className?: string
  isRequired?: boolean
  isDisabled?: boolean
  fetchOptionsParams?: Object
  /** Chamado na escolha do usuário, antes de o valor ir para o formik; recebe o que foi escolhido. */
  onChange?: (valor: IOption | IOption[] | null) => void
  infoText?: string
  labelSize?: FontSize
  highlight?: boolean
  entity?: string
  onOptionsLoaded?: (data: any) => void
  /**
   * Associa o rótulo ao combo: vai para `<Label htmlFor>` e para o `inputId` do react-select.
   * Opt-in — sem ele, nada muda no DOM (ver `ICustomSelect.inputId`).
   */
  inputId?: string
  /** O `fetchOptions` já filtra pelo texto digitado (ver `ICustomSelect.filtraNoServidor`). */
  filtraNoServidor?: boolean
}

export const FormSelect: React.FC<FormSelectProps> = ({
  name,
  label,
  isMulti,
  hideSelectAll = false,
  fetchOptions,
  placeholder,
  options,
  formik,
  isClearable,
  className = '',
  isRequired = false,
  isDisabled = false,
  fetchOptionsParams = {},
  onChange,
  infoText = '',
  labelSize,
  highlight,
  entity,
  onOptionsLoaded,
  inputId,
  filtraNoServidor,
}) => {
  const { t } = useT()

  const resolvedLabel =
    label ?? (entity ? t(`${entity}.${isMulti ? 'plural' : 'label'}`) : undefined)

  const value = getIn(formik.values, name, null)
  const touched = getIn(formik.touched, name, '')
  const errorMessage = getIn(formik.errors, name, null)
  const error = typeof errorMessage === 'string' ? errorMessage : errorMessage?.value

  return (
    <div className={`${className}`}>
      {resolvedLabel && (
        <Label
          htmlFor={inputId}
          className='mb-2'
          isRequired={isRequired}
          size={labelSize}
          infoText={infoText}
          highlight={highlight}
        >
          {resolvedLabel}
        </Label>
      )}
      <CustomSelect
        inputId={inputId}
        isMulti={isMulti}
        hideSelectAll={hideSelectAll}
        placeholder={placeholder}
        value={value}
        onChange={(values) => {
          onChange?.(values as IOption | IOption[] | null)
          formik.setFieldValue(name, values)
        }}
        onBlur={() => formik.setFieldTouched(name, true)}
        entity={entity}
        fetchOptions={fetchOptions}
        options={options}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isInvalid={Boolean(touched && error)}
        fetchOptionsParams={fetchOptionsParams}
        onOptionsLoaded={onOptionsLoaded}
        filtraNoServidor={filtraNoServidor}
      />
      {touched && error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  )
}
